import { create } from 'zustand';

export interface AgentStep {
  id: string;
  type: 'thinking' | 'tool_call_start' | 'tool_call_result' | 'text' | 'text_delta' | 'file_change' | 'plan' | 'plan_update' | 'error';
  data: any;
  timestamp: number;
}

export interface PlanState {
  title: string;
  markdown: string;
  completedSteps: string[];
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  steps?: AgentStep[];
  timestamp: number;
}

interface AIChatState {
  messages: ChatMessage[];
  isStreaming: boolean;
  currentSteps: AgentStep[];
  conversationId: string | null;
  error: string | null;
  streamingText: string;
  currentPlan: PlanState | null;

  // Actions
  addUserMessage: (content: string) => void;
  addStep: (step: Omit<AgentStep, 'id' | 'timestamp'>) => void;
  finishAssistantMessage: (content: string) => void;
  setStreaming: (streaming: boolean) => void;
  setConversationId: (id: string) => void;
  setError: (error: string | null) => void;
  clearChat: () => void;
  loadConversation: (messages: ChatMessage[], conversationId: string) => void;
  appendStreamingText: (delta: string) => void;
  clearStreamingText: () => void;
  setPlan: (plan: PlanState) => void;
  updatePlanStep: (step: string) => void;
}

function uniqueId(prefix: string): string {
  return `${prefix}-${crypto.randomUUID()}`;
}

export const useAIChatStore = create<AIChatState>((set, get) => ({
  messages: [],
  isStreaming: false,
  currentSteps: [],
  conversationId: null,
  error: null,
  streamingText: '',
  currentPlan: null,

  addUserMessage: (content) => {
    set((state) => ({
      messages: [
        ...state.messages,
        {
          id: uniqueId('user'),
          role: 'user',
          content,
          timestamp: Date.now(),
        },
      ],
      currentSteps: [],
      streamingText: '',
      error: null,
    }));
  },

  addStep: (step) => {
    const id = `step-${crypto.randomUUID()}`;
    set((state) => ({
      currentSteps: [...state.currentSteps, { ...step, id, timestamp: Date.now() }],
    }));
  },

  finishAssistantMessage: (content) => {
    const { currentSteps, streamingText } = get();
    const finalContent = content || streamingText;
    set((state) => ({
      messages: [
        ...state.messages,
        {
          id: uniqueId('assistant'),
          role: 'assistant',
          content: finalContent,
          steps: currentSteps,
          timestamp: Date.now(),
        },
      ],
      currentSteps: [],
      isStreaming: false,
      streamingText: '',
    }));
  },

  setStreaming: (streaming) => set({ isStreaming: streaming }),
  setConversationId: (id) => set({ conversationId: id }),
  setError: (error) => set({ error, isStreaming: false }),
  clearChat: () => {
    // Cancel any active stream before clearing
    import('@/services/ai-stream').then(({ cancelAgentStream }) => cancelAgentStream()).catch(() => {});
    set({ messages: [], currentSteps: [], conversationId: null, error: null, isStreaming: false, streamingText: '', currentPlan: null });
  },

  loadConversation: (messages, conversationId) => {
    set({ messages, conversationId, currentSteps: [], error: null, streamingText: '' });
  },

  appendStreamingText: (delta) => {
    set((state) => ({
      streamingText: state.streamingText + delta,
    }));
  },

  clearStreamingText: () => set({ streamingText: '' }),

  setPlan: (plan) => set({ currentPlan: plan }),

  updatePlanStep: (step) => {
    set((state) => {
      if (!state.currentPlan) return {};
      return {
        currentPlan: {
          ...state.currentPlan,
          completedSteps: [...state.currentPlan.completedSteps, step],
        },
      };
    });
  },
}));
